// No necesitamos variantes especiales, solo layoutId para FLIP

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};


import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Box } from 'lucide-react';
import {
  Search,
  X,
  ChevronDown,
  MessageCircle,
  Phone,
  Mail,
  ShoppingCart,
} from "lucide-react"; // Added icons
import { Button } from "@/components/ui/button";

import { supabase } from "@/lib/supabase";
import { useTranslation } from 'react-i18next'; // ADDED
import { trackEvent } from '@/hooks/useAnalytics';


const Products = () => {
  const { t } = useTranslation(); // ADDED
  // Estado para detectar transición de categoría
  const [lastCategory, setLastCategory] = useState("all");
  const [balonSelected, setBalonSelected] = useState({});
  // ...existing code...

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([
    { id: "all", name: t('products.allProducts') }, // TRANSLATED
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageScale, setImageScale] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Agrupación general de productos por nombre exacto
  const groupedProductsMap = {};
  products.forEach(prod => {
    if (!prod.producto) return;
    const key = prod.producto;
    if (!groupedProductsMap[key]) groupedProductsMap[key] = { ...prod, variantes: [] };
    groupedProductsMap[key].variantes.push(prod);
  });
  const groupedProducts = Object.values(groupedProductsMap);
  const [groupSelected, setGroupSelected] = useState({});


  // Phone numbers
  const phoneNumbers = [
    { number: "3338217408", display: "(33) 3821 7408" },
    { number: "3318715068", display: "(33) 1871 5068" },
  ];

  // Fetch products and categories from Supabase
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);

        // Fetch products with only the needed columns
        const { data: productsData, error: productsError } = await supabase
          .from("materia_prima")
          .select(`
            id, producto, material, diametro, 
            presentacion, textura, base, 
            disenada_para, color, imagen, categoria, hexadecimal
          `);

        if (productsError) throw productsError;

        // Obtener categorías únicas de los productos
        // Dentro del useEffect que obtiene los productos:
        const uniqueCategories = [
          ...new Set(productsData.map((p) => p.categoria).filter(Boolean)),
        ];

        const sortedCategories = [];
        const desiredOrder = [
          "AUTOMOTRIZ",
          "MUEBLE",
          "DEPORTE",
          "IMPERMEABLES",
          "LONA"
        ];

        desiredOrder.forEach(cat => {
          const foundCat = uniqueCategories.find(c => c.toUpperCase() === cat);
          if (foundCat) {
            sortedCategories.push(foundCat);
          }
        });

        const otrosCat = uniqueCategories.find(c => c.toUpperCase() === 'OTROS');
        if (otrosCat) {
          sortedCategories.push(otrosCat);
        }

        uniqueCategories.forEach(cat => {
          const catUpper = cat.toUpperCase();
          if (!sortedCategories.includes(cat) &&
            !desiredOrder.includes(catUpper) &&
            catUpper !== 'OTROS') {
            sortedCategories.push(cat);
          }
        });

        const categoryOptions = [
          { id: "all", name: t('products.allProducts') },
          ...sortedCategories.map((cat, index) => ({
            id: `cat-${index}`,
            originalName: cat,
            name: t(`products.category.${cat.toLowerCase()}`, { defaultValue: cat }),
          })),
        ];

        setProducts(productsData || []);
        setCategories(categoryOptions);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [t]);

  // Filter products based on search term and category
  const filteredProducts = products.filter((product) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      product.producto?.toLowerCase().includes(searchLower) ||
      product.material?.toLowerCase().includes(searchLower) ||
      product.descripcion?.toLowerCase().includes(searchLower) ||
      product.color?.toLowerCase().includes(searchLower);

    const matchesCategory =
      selectedCategory === "all" ||
      product.categoria ===
      categories.find((c) => c.id === selectedCategory)?.originalName;

    return matchesSearch && matchesCategory;
  });


  // Actualizar lastCategory justo antes de cambiar selectedCategory
  const handleSetCategory = (catId) => {
    setLastCategory(selectedCategory);
    setSelectedCategory(catId);
  };

  // Toggle category dropdown on mobile
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Open quote modal for a specific product
  const openQuoteModal = (product) => {
    setSelectedProduct(product);
    document.body.style.overflow = "hidden"; // Prevent scrolling when modal is open
    trackEvent('select_item', {
      items: [{ item_name: product.producto, item_category: product.categoria }],
    });
  };

  // Close quote modal
  const closeQuoteModal = () => {
    setSelectedProduct(null);
    document.body.style.overflow = "unset"; // Re-enable scrolling
  };

  // Close modal when pressing Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && selectedProduct) {
        closeQuoteModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedProduct]);

  // Generate WhatsApp URL
  const getWhatsAppUrl = (productName) => {
    const message = t('products.whatsappMessage', { productName });
    return `https://wa.me/52${phoneNumbers[0].number}?text=${encodeURIComponent(message)}`;
  };

  // Scroll handler removed

  return (
    <div className="min-h-screen bg-white">
      {/* Hero with Search */}
      <section className="relative text-white py-12 md:py-20 overflow-hidden z-10">
        {/* Fondo con patrón */}
        <div className="absolute inset-0 w-full h-full">
          <div className="absolute inset-0 hero-pattern"></div>
          {/* Capa semitransparente para mejorar contraste */}
          <div className="absolute inset-0 bg-black/20"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Animated Title */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-4"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-red-100">
                {t('products.heroTitle')}
              </h1>
              <div className="h-1 w-24 bg-white/30 mx-auto rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-white"
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ delay: 0.6, duration: 1, ease: "easeOut" }}
                />
              </div>
            </motion.div>

            {/* Enhanced Search Bar */}
            <motion.div
              className="relative max-w-2xl mx-auto mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              <div className="relative">
                <div className="absolute inset-0 rounded-xl bg-white/5 backdrop-blur-sm border border-white/20" />
                <Search
                  className="absolute left-5 top-1/2 transform -translate-y-1/2 text-red-200"
                  size={20}
                />
                <input
                  type="text"
                  placeholder={t('products.searchPlaceholder')}
                  className="w-full pl-14 pr-12 py-3 bg-transparent border-none text-white placeholder-red-200 focus:outline-none focus:ring-0 text-base relative z-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <motion.button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-red-200 hover:text-white transition-colors z-20"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X size={18} />
                  </motion.button>
                )}
              </div>
            </motion.div>

            {/* Enhanced Category Filter */}
            <motion.div
              className="w-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              {/* Desktop Categories */}
              <div className="w-full py-2 overflow-x-auto">
                <div className="flex items-center gap-3 px-4">
                  {categories.map((category) => (
                    <motion.button
                      key={category.id}
                      onClick={() => handleSetCategory(category.id)}
                      className={`whitespace-nowrap px-5 py-2 rounded-xl text-sm font-medium transition-all duration-150 flex-shrink-0 relative overflow-hidden
          ${selectedCategory === category.id
                          ? "bg-white/90 text-red-700 shadow-lg backdrop-blur-sm border border-white/20"
                          : "bg-white/5 text-white/95 hover:bg-white/10 hover:text-white backdrop-blur-sm border border-white/10 hover:border-white/20"
                        }`}
                      whileHover={{
                        y: -1,
                        scale: 1.03,
                        boxShadow:
                          "0 5px 15px -3px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                      }}
                      whileTap={{
                        scale: 0.99,
                        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.1)",
                      }}
                      layoutId={`category-${category.id}`}
                    >
                      {selectedCategory === category.id ? (
                        <span className="flex items-center">
                          <motion.span
                            className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"
                            layoutId="active-dot"
                          />
                          {category.name}
                        </span>
                      ) : (
                        <span>{category.name}</span>
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Enhanced Products Grid */}
      <section className="py-8 md:py-12 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto">
          {/* Animated Results Count */}
          <motion.div
            className="flex justify-between items-center mb-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <div className="flex items-center space-x-3">
              <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                {selectedCategory === "all"
                  ? t('products.exploreTitle')
                  : categories.find((c) => c.id === selectedCategory)?.name}
              </h2>
              <motion.span
                className="bg-red-100 text-red-800 text-xs font-semibold px-3 py-1.5 rounded-full shadow-inner"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, type: "spring" }}
              >
                {filteredProducts.length}{" "}
                {t('products.countLabel', { count: filteredProducts.length })}
              </motion.span>
            </div>
          </motion.div>

          {isLoading ? (
            <div className="flex flex-col gap-8">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl shadow-sm overflow-hidden animate-pulse"
                >
                  <div className="h-48 bg-gray-200"></div>
                  <div className="p-6">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-gray-100 rounded w-1/2 mb-2"></div>
                    <div className="h-4 bg-gray-100 rounded w-5/6 mb-4"></div>
                    <div className="h-10 bg-gray-200 rounded-lg w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <motion.div
              key={selectedCategory}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              layout
              transition={{ duration: 0.3, ease: "easeOut" }}
              className={
                selectedCategory === "all"
                  ? "flex flex-col gap-6"
                  : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              }
            >
              {/* Mostrar agrupadas solo si está en "Todos los Productos", si no, mostrar desagrupadas */}
              {selectedCategory === "all"
                ? groupedProducts.map((group, idx) => {
                  const variantes = group.variantes;
                  const selectedIdx = groupSelected[group.producto] ?? 0;
                  const selectedVar = variantes[selectedIdx] || variantes[0];
                  // Solo animar fusión/desfusión si la transición es hacia o desde "all"
                  // FLIP animation: solo layoutId, sin variantes especiales
                  return (
                    <motion.div
                      key={group.producto}
                      variants={itemVariants}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 hover:border-amber-800/20 flex flex-col md:flex-row md:items-stretch"
                      style={{ willChange: "transform, opacity" }}
                      whileHover={{
                        boxShadow: "0 10px 25px -5px rgba(100, 64, 19, 0.2)",
                        borderColor: "rgba(100, 64, 19, 0.2)",
                      }}
                    >
                      {/* Imagen con slide animado */}
                      <div
                        className="relative w-full md:w-1/3 aspect-[4/3] md:aspect-auto bg-white overflow-hidden flex items-center justify-center md:rounded-l-xl md:rounded-tr-none rounded-t-xl cursor-pointer"
                        onClick={() =>
                          selectedVar.imagen &&
                          setSelectedImage(
                            `/images/products/${selectedVar.imagen}`
                          )
                        }
                      >
                        <motion.img
                          key={selectedVar.imagen}
                          src={
                            selectedVar.imagen
                              ? `/images/products/${selectedVar.imagen}`
                              : undefined
                          }
                          alt={selectedVar.producto}
                          className="w-full h-full object-contain hover:scale-105 transition-transform duration-200"
                          initial={{ x: 40, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          exit={{ x: -40, opacity: 0 }}
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 30,
                          }}
                          loading="lazy"
                        />
                        {/* Miniatura de la textura */}
                        {selectedVar.textura_imagen && (
                          <div className="absolute bottom-2 right-2 w-10 h-10 rounded-full overflow-hidden border-2 border-white shadow-md">
                            <img
                              src={`/images/Texturas/${selectedVar.textura_imagen}`}
                              alt={selectedVar.textura}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        )}
                        {!selectedVar.imagen && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50/80 p-4">
                            <Box className="h-16 w-16 text-gray-300" />
                            <span className="mt-2 text-sm text-gray-400 text-center">
                              {selectedVar.producto}
                            </span>
                          </div>
                        )}
                      </div>
                      {/* Info */}
                      <div className="flex-1 flex flex-col p-5 justify-between">
                        <div className="space-y-2">
                          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                            {selectedVar.producto}
                          </h3>
                          {selectedVar.categoria && (
                            <span className="inline-block px-3 py-1 bg-red-50 text-red-700 text-xs font-medium rounded-full">
                              {t(`products.category.${selectedVar.categoria.toLowerCase()}`, { defaultValue: selectedVar.categoria })}
                            </span>
                          )}
                        </div>
                        {/* Selector de color */}
                        {variantes.length > 1 && (
                          <div className="flex flex-wrap gap-2 mt-3 mb-2 items-center">
                            <span className="text-xs text-gray-500 mr-2 min-w-[60px]">
                              {t('products.colorsLabel')}
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {variantes.map((v, vIdx) => (
                                <span
                                  key={vIdx}
                                  className={`w-7 h-7 rounded-full border-2 ${selectedIdx === vIdx
                                    ? "border-red-500 ring-2 ring-red-300"
                                    : "border-gray-300"
                                    } cursor-pointer transition-all duration-200 flex items-center justify-center`}
                                  style={{
                                    backgroundColor:
                                      v.hexadecimal ||
                                      v.color?.toLowerCase() ||
                                      "#eee",
                                  }}
                                  title={v.color}
                                  onClick={() =>
                                    setGroupSelected((s) => ({
                                      ...s,
                                      [group.producto]: vIdx,
                                    }))
                                  }
                                >
                                  {selectedIdx === vIdx && (
                                    <span className="block w-3 h-3 rounded-full border-2 border-white bg-white/40" />
                                  )}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Detalles */}
                        <div className="mt-3 space-y-2 text-sm text-gray-600 pr-2">
                          {Object.entries(selectedVar)
                            .filter(
                              ([key]) =>
                                key.toLowerCase() !== "imagen" &&
                                key !== "id" &&
                                key !== "hexadecimal" &&
                                key !== "variantes"
                            )
                            .map(([key, value]) => {
                              if (!value) return null;
                              const specialFields = ["producto", "categoria"];
                              if (specialFields.includes(key)) return null;
                              let fieldName = t(`products.fields.${key}`, {
                                defaultValue: key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
                              });
                              if (key.toLowerCase() === "color") {
                                return (
                                  <div
                                    key={key}
                                    className="flex items-center space-x-2"
                                  >
                                    <span className="font-medium text-gray-500 w-24 flex-shrink-0">
                                      {fieldName}:
                                    </span>
                                    <div className="flex items-center">
                                      <span
                                        className="w-5 h-5 rounded-full border border-gray-300 mr-2"
                                        style={{
                                          backgroundColor:
                                            selectedVar.hexadecimal ||
                                            value.toLowerCase(),
                                          flexShrink: 0,
                                        }}
                                      />
                                      <span>{t(`products.specs.${String(value).toLowerCase().replace(/\s+/g, '_')}`, { defaultValue: String(value) })}</span>
                                    </div>
                                  </div>
                                );
                              } else {
                                return (
                                  <div key={key} className="flex items-start">
                                    <span className="font-medium text-gray-500 w-24 flex-shrink-0">
                                      {fieldName}:
                                    </span>
                                    <span className="flex-1 break-words">
                                      {t(`products.specs.${String(value).toLowerCase().replace(/\s+/g, '_')}`, { defaultValue: String(value) })}
                                    </span>
                                  </div>
                                );
                              }
                            })}
                        </div>
                        {/* Botón de cotizar */}
                        <div className="mt-6">
                          <button
                            onClick={() => openQuoteModal(selectedVar)}
                            className="button-animate w-full py-3 px-6 text-lg font-medium text-white rounded-md border-2 border-transparent transition-all duration-300 hover:opacity-80 hover:shadow-none flex items-center justify-center gap-2"
                            style={{
                              background:
                                "linear-gradient(140.14deg, #e11d48 15.05%, #be123c 114.99%) padding-box, linear-gradient(142.51deg, #fb7185 8.65%, #9f1239 88.82%) border-box",
                              textShadow: "1px 1px 1px #00000040",
                              boxShadow:
                                "8px 8px 20px 0px rgba(190, 18, 60, 0.35)",
                            }}
                          >
                            <ShoppingCart className="h-5 w-5 icon-wiggle" />
                            {t('products.requestInfo')}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
                : filteredProducts.map((product, idx) => (
                  <motion.div
                    key={product.id}
                    variants={itemVariants}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 hover:border-amber-800/20 flex flex-col"
                    style={{ willChange: "transform, opacity" }}
                    whileHover={{
                      boxShadow: "0 10px 25px -5px rgba(100, 64, 19, 0.2)",
                      borderColor: "rgba(100, 64, 19, 0.2)",
                    }}
                  >
                    {/* Imagen */}
                    <div
                      className="relative w-full aspect-[4/3] bg-white overflow-hidden flex items-center justify-center rounded-t-xl cursor-pointer"
                      onClick={() =>
                        product.imagen &&
                        setSelectedImage(`/images/products/${product.imagen}`)
                      }
                    >
                      <motion.img
                        key={product.imagen}
                        src={
                          product.imagen
                            ? `/images/products/${product.imagen}`
                            : undefined
                        }
                        alt={product.producto}
                        className="w-full h-full object-contain hover:scale-105 transition-transform duration-200"
                        initial={{ x: 40, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -40, opacity: 0 }}
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 30,
                        }}
                        loading="lazy"
                      />
                      {/* Miniatura de la textura */}
                      {product.textura_imagen && (
                        <div className="absolute bottom-2 right-2 w-10 h-10 rounded-full overflow-hidden border-2 border-white shadow-md">
                          <img
                            src={`/images/Texturas/${product.textura_imagen}`}
                            alt={product.textura}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      )}
                      {!product.imagen && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50/80 p-4">
                          <Box className="h-16 w-16 text-gray-300" />
                          <span className="mt-2 text-sm text-gray-400 text-center">
                            {product.producto}
                          </span>
                        </div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="flex-1 flex flex-col p-5 justify-between">
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                          {product.producto}
                        </h3>
                        {product.categoria && (
                          <span className="inline-block px-3 py-1 bg-red-50 text-red-700 text-xs font-medium rounded-full">
                            {t(`products.category.${product.categoria.toLowerCase()}`, { defaultValue: product.categoria })}
                          </span>
                        )}
                      </div>
                      {/* Detalles */}
                      <div className="mt-3 space-y-2 text-sm text-gray-600 pr-2">
                        {Object.entries(product)
                          .filter(
                            ([key]) =>
                              key.toLowerCase() !== "imagen" &&
                              key !== "id" &&
                              key !== "hexadecimal" &&
                              key !== "variantes"
                          )
                          .map(([key, value]) => {
                            if (!value) return null;
                            const specialFields = ["producto", "categoria"];
                            if (specialFields.includes(key)) return null;
                            let fieldName = key
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase());
                            if (key === "disenada_para") {
                              fieldName = "Diseñada Para";
                            }
                            if (key.toLowerCase() === "color") {
                              return (
                                <div
                                  key={key}
                                  className="flex items-center space-x-2"
                                >
                                  <span className="font-medium text-gray-500 w-24 flex-shrink-0">
                                    {fieldName}:
                                  </span>
                                  <div className="flex items-center">
                                    <span
                                      className="w-5 h-5 rounded-full border border-gray-300 mr-2"
                                      style={{
                                        backgroundColor:
                                          product.hexadecimal ||
                                          value.toLowerCase(),
                                        flexShrink: 0,
                                      }}
                                    />
                                    <span>{t(`products.specs.${String(value).toLowerCase().replace(/\s+/g, '_')}`, { defaultValue: String(value) })}</span>
                                  </div>
                                </div>
                              );
                            } else {
                              return (
                                <div key={key} className="flex items-start">
                                  <span className="font-medium text-gray-500 w-24 flex-shrink-0">
                                    {fieldName}:
                                  </span>
                                  <span className="flex-1 break-words">
                                    {t(`products.specs.${String(value).toLowerCase().replace(/\s+/g, '_')}`, { defaultValue: String(value) })}
                                  </span>
                                </div>
                              );
                            }
                          })}
                      </div>
                      {/* Botón de cotizar */}
                      <div className="mt-6">
                        <button
                          onClick={() => openQuoteModal(product)}
                          className="button-animate w-full py-3 px-6 text-lg font-medium text-white rounded-md border-2 border-transparent transition-all duration-300 hover:opacity-80 hover:shadow-none flex items-center justify-center gap-2"
                          style={{
                            background:
                              "linear-gradient(140.14deg, #e11d48 15.05%, #be123c 114.99%) padding-box, linear-gradient(142.51deg, #fb7185 8.65%, #9f1239 88.82%) border-box",
                            textShadow: "1px 1px 1px #00000040",
                            boxShadow:
                              "8px 8px 20px 0px rgba(190, 18, 60, 0.35)",
                          }}
                        >
                          <ShoppingCart className="h-5 w-5 icon-wiggle" />
                          {t('products.requestInfo')}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}

              {/* Empty State */}
              {filteredProducts.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="col-span-full text-center py-16 px-4"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 text-red-500 mb-4">
                    <Search className="h-8 w-8" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    {t('products.noProductsTitle')}
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto mb-6">
                    {t('products.noProductsDescription')}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedCategory("all");
                    }}
                    className="transition-all hover:bg-red-50 hover:text-red-700"
                  >
                    <X className="h-4 w-4 mr-2" />
                    {t('products.clearFilters')}
                  </Button>
                </motion.div>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 text-white relative overflow-hidden">
        {/* Fondo con patrón */}
        <div className="absolute inset-0 w-full h-full">
          <div className="absolute inset-0 hero-pattern"></div>
          {/* Capa semitransparente para mejorar contraste */}
          <div className="absolute inset-0 bg-black/20"></div>
        </div>
        <div className="relative z-10">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-4">
                {t('products.notFoundTitle')}
              </h2>
              <p className="text-xl mb-6 opacity-90"></p>
              <div className="text-center">
                <Link to="/contacto" className="inline-block">
                  <button className="modern-button group">
                    {t('products.contactButton')}
                    <span className="arrow-container opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="animate-bounce-x ml-2 h-5 w-5 inline-block"
                      >
                        <circle cx="8" cy="21" r="1"></circle>
                        <circle cx="19" cy="21" r="1"></circle>
                        <path d="M21.2 8.4c.5.38.8.97.8 1.6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h3.3a2 2 0 0 0 1.4-.6L12 3"></path>
                        <path d="m22 10-10 7-10-7"></path>
                      </svg>
                    </span>
                  </button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
            onClick={() => {
              setSelectedImage(null);
              setImageScale(1);
              setImagePosition({ x: 0, y: 0 });
            }}
          >
            <div
              className="max-w-full max-h-full flex items-center justify-center overflow-hidden"
              onWheel={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const delta = e.deltaY > 0 ? -0.2 : 0.2;
                setImageScale((prev) =>
                  Math.max(0.5, Math.min(4, prev + delta))
                );
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage}
                alt="Producto"
                className={`object-contain transition-transform duration-200 ${isDragging ? "cursor-grabbing" : "cursor-grab"
                  }`}
                style={{
                  transform: `scale(${imageScale}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                  setDragStart({
                    x: e.clientX - imagePosition.x,
                    y: e.clientY - imagePosition.y,
                  });
                }}
                onMouseMove={(e) => {
                  if (!isDragging) return;
                  setImagePosition({
                    x: e.clientX - dragStart.x,
                    y: e.clientY - dragStart.y,
                  });
                }}
                onMouseUp={() => setIsDragging(false)}
                onMouseLeave={() => setIsDragging(false)}
                draggable={false}
              />
            </div>
            <button
              onClick={() => {
                setSelectedImage(null);
                setImageScale(1);
                setImagePosition({ x: 0, y: 0 });
              }}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            >
              <X size={32} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quote Modal */}
      <AnimatePresence>
        {selectedProduct && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4"
              onClick={closeQuoteModal}
            >
              {/* Modal Content */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-auto my-4 max-h-[90vh] overflow-y-auto border border-red-200 flex flex-col md:flex-row"
                style={{
                  backgroundImage: `linear-gradient(to bottom right, white, #fff5f5, white)`,
                  boxShadow: `0 25px 50px -12px rgba(174, 4, 4, 0.25)`,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Columna izquierda: Información del producto */}
                <div className="w-full md:w-5/12 p-6 border-r border-red-100">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-gray-900">
                      {t('products.quoteModal.title')}
                    </h3>
                    <button
                      onClick={closeQuoteModal}
                      className="text-gray-400 hover:text-gray-600 transition-colors md:hidden"
                      aria-label={t('products.quoteModal.closeAria')}
                    >
                      <X size={24} />
                    </button>
                  </div>

                  <div className="flex flex-col items-center">
                    {/* Imagen del producto */}
                    {selectedProduct.imagen && (
                      <div className="w-40 h-40 bg-white rounded-xl overflow-hidden border border-red-100 shadow-md mb-4">
                        <img
                          src={`/images/products/${selectedProduct.imagen}`}
                          alt={selectedProduct.producto}
                          className="w-full h-full object-contain p-2"
                        />
                      </div>
                    )}

                    <div className="text-center">
                      <h4 className="font-bold text-lg text-gray-900 mb-1">
                        {selectedProduct.producto}
                      </h4>

                      {selectedProduct.material && (
                        <p className="text-sm text-gray-600">
                          {t('products.quoteModal.material')} {selectedProduct.material}
                        </p>
                      )}

                      {selectedProduct.color && (
                        <div className="flex items-center justify-center mt-1">
                          <span className="text-sm text-gray-600 mr-2">
                            {t('products.quoteModal.color')}
                          </span>
                          <span
                            className="w-5 h-5 rounded-full border border-gray-300 inline-block"
                            style={{
                              backgroundColor:
                                selectedProduct.hexadecimal ||
                                selectedProduct.color.toLowerCase(),
                            }}
                          />
                          <span className="text-sm ml-1">
                            {selectedProduct.color}
                          </span>
                        </div>
                      )}

                      {selectedProduct.descripcion && (
                        <p className="text-sm text-gray-500 mt-2 max-w-xs">
                          {selectedProduct.descripcion}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Columna derecha: Botones de contacto */}
                <div className="w-full md:w-7/12 p-6 flex flex-col justify-center">
                  <button
                    onClick={closeQuoteModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors absolute top-4 right-4 hidden md:block"
                    aria-label="Cerrar"
                  >
                    <X size={28} />
                  </button>

                  <div className="space-y-4">
                    <a
                      href={getWhatsAppUrl(selectedProduct.producto)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="button-animate flex items-center justify-center gap-3 py-4 px-6 text-sm font-medium rounded-xl bg-green-50 text-green-700 border border-green-200 transition-all duration-300 hover:bg-green-100 hover:shadow-md"
                    >
                      <img
                        src="/images/Whatsapp_icon.svg"
                        alt="WhatsApp"
                        className="h-6 w-6"
                      />
                      <span className="font-semibold">
                        {t('products.quoteModal.whatsapp')}
                      </span>
                    </a>

                    {phoneNumbers.map((phone, index) => (
                      <a
                        key={index}
                        href={`tel:${phone.number}`}
                        className="button-animate flex items-center justify-center gap-3 py-4 px-6 text-sm font-medium rounded-xl bg-blue-50 text-blue-700 border border-blue-200 transition-all duration-300 hover:bg-blue-100 hover:shadow-md"
                      >
                        <Phone className="h-5 w-5" />
                        <span className="font-semibold">
                          {t('products.quoteModal.call', { display: phone.display })}
                        </span>
                      </a>
                    ))}

                    <Link
                      to="/contacto"
                      onClick={closeQuoteModal}
                      className="button-animate flex items-center justify-center gap-3 py-4 px-6 text-sm font-medium rounded-xl bg-red-50 text-red-700 border border-red-200 transition-all duration-300 hover:bg-red-100 hover:shadow-md"
                    >
                      <Mail className="h-5 w-5" />
                      <span className="font-semibold">
                        {t('products.quoteModal.email')}
                      </span>
                    </Link>
                  </div>

                  <p className="text-xs text-center mt-6 text-gray-500">
                    {t('products.quoteModal.footer')}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Quote Modal - Versión responsiva para Products.jsx */}
      <AnimatePresence>
        {selectedProduct && (
          <>
            {/* Overlay - zIndex corregido para que aparezca por encima de TODO */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-2 md:p-4"
              onClick={closeQuoteModal}
            >
              {/* Modal Content - RESPONSIVE */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="bg-white rounded-xl shadow-2xl w-full mx-auto my-2 max-h-[95vh] overflow-y-auto md:max-w-4xl md:max-h-[90vh] border border-red-200 flex flex-col md:flex-row"
                style={{
                  backgroundImage: `linear-gradient(to bottom right, white, #fff5f5, white)`,
                  boxShadow: `0 25px 50px -12px rgba(174, 4, 4, 0.25)`,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Columna izquierda: Información del producto - RESPONSIVE */}
                <div className="w-full md:w-5/12 p-4 md:p-6 border-b md:border-b-0 md:border-r border-red-100">
                  <div className="flex justify-between items-start mb-3 md:mb-4">
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                      {t('products.quoteModal.title')}
                    </h3>
                    {/* Botón de cerrar visible solo en móviles */}
                    <button
                      onClick={closeQuoteModal}
                      className="text-gray-400 hover:text-gray-600 transition-colors md:hidden"
                      aria-label={t('products.quoteModal.closeAria')}
                    >
                      <X size={20} md:size={24} />
                    </button>
                  </div>

                  <div className="flex flex-col items-center">
                    {/* Imagen del producto - Mejorada para móvil */}
                    <div className="w-32 h-32 md:w-40 md:h-40 bg-white rounded-xl overflow-hidden border border-red-100 shadow-md mb-3 md:mb-4">
                      <img
                        src={`/images/products/${selectedProduct.imagen}`}
                        alt={selectedProduct.producto}
                        className="w-full h-full object-contain p-1 md:p-2"
                      />
                    </div>

                    <div className="text-center w-full px-1 md:px-2">
                      <h4 className="font-bold text-lg md:text-xl text-gray-900 mb-1 md:mb-2">
                        {selectedProduct.producto}
                      </h4>

                      {selectedProduct.material && (
                        <p className="text-sm md:text-base text-gray-600 mb-1">
                          {t('products.quoteModal.material')} {selectedProduct.material}
                        </p>
                      )}

                      {selectedProduct.color && (
                        <div className="flex items-center justify-center mt-1 md:mt-2 mb-2 md:mb-3">
                          <span className="text-xs md:text-sm text-gray-600 mr-1 md:mr-2">
                            {t('products.quoteModal.color')}
                          </span>
                          <span
                            className="w-4 h-4 md:w-5 md:h-5 rounded-full border border-gray-300 inline-block"
                            style={{
                              backgroundColor:
                                selectedProduct.hexadecimal ||
                                selectedProduct.color.toLowerCase(),
                            }}
                          />
                          <span className="text-xs md:text-sm ml-1 md:ml-2">
                            {selectedProduct.color}
                          </span>
                        </div>
                      )}

                      {selectedProduct.descripcion && (
                        <p className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2 max-w-xs px-1 md:px-0">
                          {selectedProduct.descripcion}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Columna derecha: Botones de contacto - RESPONSIVE */}
                <div className="w-full md:w-7/12 p-4 md:p-6 flex flex-col justify-center">
                  {/* Botón de cerrar visible solo en desktop */}
                  <button
                    onClick={closeQuoteModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors absolute top-3 right-3 md:top-4 md:right-4 hidden md:block"
                    aria-label="Cerrar"
                  >
                    <X size={20} md:size={24} />
                  </button>

                  <div className="space-y-3 md:space-y-4">
                    <a
                      href={getWhatsAppUrl(selectedProduct.producto)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="button-animate flex items-center justify-center gap-2 md:gap-3 py-3 md:py-4 px-4 md:px-6 text-xs md:text-sm font-medium rounded-lg md:rounded-xl bg-green-50 text-green-700 border border-green-200 transition-all duration-300 hover:bg-green-100 hover:shadow-md"
                    >
                      <img
                        src="/images/Whatsapp_icon.svg"
                        alt="WhatsApp"
                        className="h-4 w-4 md:h-6 md:w-6"
                      />
                      <span className="font-semibold text-xs md:text-sm">
                        {t('products.quoteModal.whatsapp')}
                      </span>
                    </a>

                    {phoneNumbers.map((phone, index) => (
                      <a
                        key={index}
                        href={`tel:${phone.number}`}
                        className="button-animate flex items-center justify-center gap-2 md:gap-3 py-3 md:py-4 px-4 md:px-6 text-xs md:text-sm font-medium rounded-lg md:rounded-xl bg-blue-50 text-blue-700 border border-blue-200 transition-all duration-300 hover:bg-blue-100 hover:shadow-md"
                      >
                        <Phone className="h-4 w-4 md:h-5 md:w-5" />
                        <span className="font-semibold text-xs md:text-sm">
                          {t('products.quoteModal.call', { display: phone.display })}
                        </span>
                      </a>
                    ))}

                    <Link
                      to="/contacto"
                      onClick={closeQuoteModal}
                      className="button-animate flex items-center justify-center gap-2 md:gap-3 py-3 md:py-4 px-4 md:px-6 text-xs md:text-sm font-medium rounded-lg md:rounded-xl bg-red-50 text-red-700 border border-red-200 transition-all duration-300 hover:bg-red-100 hover:shadow-md"
                    >
                      <Mail className="h-4 w-4 md:h-5 md:w-5" />
                      <span className="font-semibold text-xs md:text-sm">
                        {t('products.quoteModal.email')}
                      </span>
                    </Link>
                  </div>

                  <p className="text-[0.65rem] md:text-xs text-center mt-3 md:mt-6 text-gray-500 px-1">
                    {t('products.quoteModal.footer')}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Products;